import { useMemo, useState } from 'react';
import { Check, Plus, Trash2, X } from 'lucide-react';
import { useSelector } from 'react-redux';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { useUpdateWorkAssignmentListAttachment } from 'components/IAP/hooks/assignmentListHooks';
import {
  useCreateIncidentComs,
  useIncidentComs,
  useUpdateIncidentComs,
  type IncidentComs,
} from 'components/IAP/hooks/incidentComsHooks';
import { INCIDENT } from 'components/IAP/ICSConstants';
import {
  buildContactInfoFromIcs205Com,
  getComDisplayName,
} from 'components/IAP/utils/workAssignmentUtils';
import { NULL_VALUE } from 'constants/displayConstants';
import { selectGroupGuid } from 'slices/commonSelectors';
import {
  emptyCommunicationDraft,
  type CommunicationDraft,
} from './assignmentCardFormTypes';
import {
  ICS204_DELETE_BTN,
  ICS204_HINT,
  ICS204_ICON_BTN,
  ICS204_MUTED,
  ICS204_OUTLINE_BTN,
  ICS204_SECTION_TITLE,
  ICS204_TABLE,
} from './assignmentCardStyles';
import { Ics204ReadOnlyField } from './Ics204Fields';
import { Ics204DisabledActionTooltip } from './Ics204DisabledActionTooltip';

type CommunicationsSectionProps = {
  workGroupId: string;
  walaId: string | null;
  canEdit: boolean;
  disabledReason?: string | null;
  medicalCommunication?: string;
  evacuationCommunication?: string;
  otherCommunication?: string;
};

const COLUMNS = [
  'Type *',
  'Function / Name *',
  'Channel Name *',
  'Channel #',
  'RX Freq',
  'RX Tone/NAC',
  'TX Freq',
  'TX Tone/NAC',
  'Mode',
  'Remarks',
  '',
];

const isNonradio = (type: string | undefined) =>
  (type ?? '').toLowerCase() === 'nonradio';

const toDraft = (com: IncidentComs): CommunicationDraft => ({
  id: com.id,
  archived: false,
  type: com.type ?? 'Radio',
  channel_number: com.channel_number ?? '',
  function: com.function ?? '',
  channel_name: com.channel_name ?? '',
  work_group_id: com.work_group_id ?? '',
  rx_frequency: com.rx_frequency ?? '',
  rx_tone_nac: com.rx_tone_nac ?? '',
  tx_frequency: com.tx_frequency ?? '',
  tx_tone_nac: com.tx_tone_nac ?? '',
  mode: com.mode ?? '',
  remarks: com.remarks ?? '',
  cell_phone_number: com.cell_phone_number ?? '',
  other: com.other ?? '',
});

/**
 * Box 8 on the tip path: persistent Add, per-row Check/Cancel, and a separate
 * Emergency Communication edit block. No section-level edit chrome.
 */
const CommunicationsSection = ({
  workGroupId,
  walaId,
  canEdit,
  disabledReason = null,
  medicalCommunication,
  evacuationCommunication,
  otherCommunication,
}: CommunicationsSectionProps) => {
  const groupGuid = useSelector(selectGroupGuid);
  const { data: allIncidentComs, isPending } = useIncidentComs(INCIDENT);
  const { mutateAsync: createComs } = useCreateIncidentComs();
  const { mutateAsync: updateComs } = useUpdateIncidentComs();
  const { mutateAsync: updateWala } = useUpdateWorkAssignmentListAttachment();

  const rows = useMemo(
    () =>
      (allIncidentComs ?? []).filter(
        (com) => com.work_group_id === workGroupId && !com.archived
      ),
    [allIncidentComs, workGroupId]
  );

  const [editingKey, setEditingKey] = useState<string | 'new' | null>(null);
  const [draft, setDraft] = useState<CommunicationDraft | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);
  const [isSavingRow, setIsSavingRow] = useState(false);

  const [editingEmergency, setEditingEmergency] = useState(false);
  const [emergencyDraft, setEmergencyDraft] = useState({
    medical: '',
    evacuation: '',
    other: '',
  });
  const [isSavingEmergency, setIsSavingEmergency] = useState(false);

  const touchTip = async () => {
    if (!walaId) return;
    await updateWala({ walaId, touch: true });
  };

  const startAdd = () => {
    if (!canEdit || isSavingRow) return;
    setRowError(null);
    setEditingKey('new');
    setDraft(emptyCommunicationDraft());
  };

  const startEditRow = (com: IncidentComs) => {
    if (!canEdit || isSavingRow) return;
    setRowError(null);
    setEditingKey(com.id);
    setDraft(toDraft(com));
  };

  const cancelRow = () => {
    setEditingKey(null);
    setDraft(null);
    setRowError(null);
  };

  const confirmRow = async () => {
    if (!draft) return;
    const type = (draft.type ?? '').trim();
    const nameOrFunction = isNonradio(draft.type)
      ? (draft.other ?? '').trim()
      : (draft.function ?? '').trim();
    const channelName = (draft.channel_name ?? '').trim();
    if (!type || !nameOrFunction || !channelName) {
      setRowError('Type, Function / Name, and Channel Name are required.');
      return;
    }
    setRowError(null);
    setIsSavingRow(true);
    try {
      if (!draft.id) {
        const { id: _id, archived: _archived, ...fields } = draft;
        await createComs({
          ...fields,
          type: fields.type ?? 'Radio',
          archived: false,
          group_guid: groupGuid,
          work_group_id: workGroupId,
        });
      } else {
        const server = rows.find((r) => r.id === draft.id);
        if (!server) return;
        const { id, archived, ...fields } = draft;
        const { assignment: _assignment, ...serverWithoutAssignment } = server;
        await updateComs({
          comsId: server.id,
          data: {
            ...serverWithoutAssignment,
            ...fields,
            type: fields.type ?? server.type ?? 'Radio',
            assignment: undefined,
            archived: false,
          },
        });
      }
      await touchTip();
      cancelRow();
    } catch {
      // Mutation hooks toast.
    } finally {
      setIsSavingRow(false);
    }
  };

  const archiveRow = async (com: IncidentComs) => {
    if (!canEdit || isSavingRow) return;
    setIsSavingRow(true);
    try {
      const { assignment: _assignment, ...serverWithoutAssignment } = com;
      await updateComs({
        comsId: com.id,
        data: {
          ...serverWithoutAssignment,
          assignment: undefined,
          archived: true,
        },
      });
      await touchTip();
      if (editingKey === com.id) cancelRow();
    } catch {
      // Mutation hooks toast.
    } finally {
      setIsSavingRow(false);
    }
  };

  const startEmergencyEdit = () => {
    if (!canEdit || isSavingEmergency) return;
    setEditingEmergency(true);
    setEmergencyDraft({
      medical: medicalCommunication ?? '',
      evacuation: evacuationCommunication ?? '',
      other: otherCommunication ?? '',
    });
  };

  const cancelEmergency = () => {
    setEditingEmergency(false);
  };

  const confirmEmergency = async () => {
    if (!walaId) return;
    setIsSavingEmergency(true);
    try {
      await updateWala({
        walaId,
        medicalCommunication: emergencyDraft.medical,
        evacuationCommunication: emergencyDraft.evacuation,
        otherCommunication: emergencyDraft.other,
        touch: true,
      });
      setEditingEmergency(false);
    } catch {
      // Mutation hooks toast.
    } finally {
      setIsSavingEmergency(false);
    }
  };

  const patchDraft = (patch: Partial<CommunicationDraft>) => {
    setRowError(null);
    setDraft((prev) => (prev ? { ...prev, ...patch } : prev));
  };

  if (isPending) {
    return <div className={ICS204_MUTED}>Loading communications…</div>;
  }

  const showEmpty = rows.length === 0 && editingKey !== 'new';

  return (
    <div data-testid="ics204-edit-communications">
      <p className={ICS204_HINT}>These channels are shared with the ICS-205.</p>
      {(() => {
        const addButton = (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className={cn(ICS204_OUTLINE_BTN, 'tw:mt-2')}
            data-testid="ics204-edit-add-communication"
            disabled={!canEdit || isSavingRow || editingKey === 'new'}
            onClick={startAdd}
          >
            <Plus className="tw:h-4 tw:w-4" />
            Add Communication
          </Button>
        );
        return !canEdit && disabledReason ? (
          <Ics204DisabledActionTooltip reason={disabledReason}>
            {addButton}
          </Ics204DisabledActionTooltip>
        ) : (
          addButton
        );
      })()}

      <Table
        data-testid="ics204-communications-table"
        className={cn('tw:mt-2', ICS204_TABLE)}
      >
        <TableHeader>
          <TableRow>
            {canEdit ? (
              COLUMNS.map((label, idx) => (
                <TableHead key={label || `actions-${idx}`}>{label}</TableHead>
              ))
            ) : (
              <>
                <TableHead>Name / Function</TableHead>
                <TableHead>Contact Information</TableHead>
              </>
            )}
          </TableRow>
        </TableHeader>
        <TableBody>
          {editingKey === 'new' && draft ? (
            <CommunicationEditRow
              draft={draft}
              disabled={isSavingRow}
              error={rowError}
              onChange={patchDraft}
              onConfirm={confirmRow}
              onCancel={cancelRow}
            />
          ) : null}
          {showEmpty ? (
            <TableRow>
              <TableCell
                colSpan={canEdit ? COLUMNS.length : 2}
                className={ICS204_MUTED}
              >
                No communications
              </TableCell>
            </TableRow>
          ) : (
            rows.map((com) =>
              editingKey === com.id && draft ? (
                <CommunicationEditRow
                  key={com.id}
                  draft={draft}
                  disabled={isSavingRow}
                  error={rowError}
                  onChange={patchDraft}
                  onConfirm={confirmRow}
                  onCancel={cancelRow}
                />
              ) : canEdit ? (
                <CommunicationViewRow
                  key={com.id}
                  com={com}
                  disabled={isSavingRow}
                  onEdit={() => startEditRow(com)}
                  onDelete={() => void archiveRow(com)}
                />
              ) : (
                <TableRow key={com.id} data-testid="ics204-communications-row">
                  <TableCell>{getComDisplayName(com)}</TableCell>
                  <TableCell>
                    {buildContactInfoFromIcs205Com(com) || NULL_VALUE}
                  </TableCell>
                </TableRow>
              )
            )
          )}
        </TableBody>
      </Table>

      <div
        className="tw:mt-3"
        data-testid="ics204-emergency-communications"
        data-ics204-emergency-editing={editingEmergency ? 'true' : undefined}
      >
        <div className={`${ICS204_SECTION_TITLE}`}>
          Emergency Communications:
        </div>
        {editingEmergency ? (
          <div className="tw:mt-2">
            <div className="tw:grid tw:grid-cols-1 tw:gap-4 tw:md:grid-cols-3">
              <EmergencyField
                label="Medical Communication"
                testId="ics204-edit-medical-communication"
                value={emergencyDraft.medical}
                disabled={isSavingEmergency}
                onChange={(medical) =>
                  setEmergencyDraft((prev) => ({ ...prev, medical }))
                }
              />
              <EmergencyField
                label="Evacuation Communication"
                testId="ics204-edit-evacuation-communication"
                value={emergencyDraft.evacuation}
                disabled={isSavingEmergency}
                onChange={(evacuation) =>
                  setEmergencyDraft((prev) => ({ ...prev, evacuation }))
                }
              />
              <EmergencyField
                label="Other Communication"
                testId="ics204-edit-other-communication"
                value={emergencyDraft.other}
                disabled={isSavingEmergency}
                onChange={(other) =>
                  setEmergencyDraft((prev) => ({ ...prev, other }))
                }
              />
            </div>
            <div className="tw:mt-2 tw:flex tw:justify-end tw:gap-1">
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                aria-label="Confirm emergency communications"
                className={ICS204_ICON_BTN}
                data-testid="ics204-edit-confirm-emergency"
                disabled={isSavingEmergency}
                onClick={() => void confirmEmergency()}
              >
                <Check className="tw:h-4 tw:w-4" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                aria-label="Cancel emergency communications"
                className={ICS204_DELETE_BTN}
                data-testid="ics204-edit-cancel-emergency"
                disabled={isSavingEmergency}
                onClick={cancelEmergency}
              >
                <X className="tw:h-4 tw:w-4" />
              </Button>
            </div>
          </div>
        ) : (
          <div
            className={cn(
              'tw:mt-2 tw:grid tw:grid-cols-1 tw:gap-4 tw:md:grid-cols-3',
              canEdit && 'tw:cursor-pointer'
            )}
            onClick={startEmergencyEdit}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                startEmergencyEdit();
              }
            }}
            role={canEdit ? 'button' : undefined}
            tabIndex={canEdit ? 0 : undefined}
          >
            <Ics204ReadOnlyField
              title="Medical"
              label="Medical"
              value={medicalCommunication}
              testId="ics204-emergency-medical"
            />
            <Ics204ReadOnlyField
              title="Evacuation"
              label="Evacuation"
              value={evacuationCommunication}
              testId="ics204-emergency-evacuation"
            />
            <Ics204ReadOnlyField
              title="Other"
              label="Other"
              value={otherCommunication}
              testId="ics204-emergency-other"
            />
          </div>
        )}
      </div>
    </div>
  );
};

const EmergencyField = ({
  label,
  testId,
  value,
  disabled,
  onChange,
}: {
  label: string;
  testId: string;
  value: string;
  disabled: boolean;
  onChange: (value: string) => void;
}) => (
  <div>
    <div className="tw:mb-1 tw:text-xs tw:text-muted-foreground">{label}</div>
    <Input
      value={value}
      disabled={disabled}
      data-testid={testId}
      onChange={(e) => onChange(e.target.value)}
    />
  </div>
);

const dash = (value: string | null | undefined) =>
  value?.trim() ? value : '—';

const CommunicationViewRow = ({
  com,
  disabled,
  onEdit,
  onDelete,
}: {
  com: IncidentComs;
  disabled: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) => {
  const radioOnly = !isNonradio(com.type);
  return (
    <TableRow
      data-testid="ics204-communications-row"
      className="tw:cursor-pointer"
      onClick={onEdit}
    >
      <TableCell>{dash(com.type)}</TableCell>
      <TableCell>{dash(radioOnly ? com.function : com.other)}</TableCell>
      <TableCell>{dash(com.channel_name)}</TableCell>
      <TableCell>
        {dash(radioOnly ? com.channel_number : com.cell_phone_number)}
      </TableCell>
      <TableCell>{radioOnly ? dash(com.rx_frequency) : '—'}</TableCell>
      <TableCell>{radioOnly ? dash(com.rx_tone_nac) : '—'}</TableCell>
      <TableCell>{radioOnly ? dash(com.tx_frequency) : '—'}</TableCell>
      <TableCell>{radioOnly ? dash(com.tx_tone_nac) : '—'}</TableCell>
      <TableCell>{radioOnly ? dash(com.mode) : '—'}</TableCell>
      <TableCell>{dash(com.remarks)}</TableCell>
      <TableCell>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label="Delete communication"
          className={ICS204_DELETE_BTN}
          data-testid="ics204-edit-delete-communication"
          disabled={disabled}
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
        >
          <Trash2 className="tw:h-4 tw:w-4" />
        </Button>
      </TableCell>
    </TableRow>
  );
};

const CommunicationEditRow = ({
  draft,
  disabled,
  error,
  onChange,
  onConfirm,
  onCancel,
}: {
  draft: CommunicationDraft;
  disabled: boolean;
  error: string | null;
  onChange: (patch: Partial<CommunicationDraft>) => void;
  onConfirm: () => void;
  onCancel: () => void;
}) => {
  const radioOnly = !isNonradio(draft.type);

  return (
    <>
      <TableRow data-testid="ics204-edit-communication-row">
        <TableCell>
          <Select
            value={draft.type ?? 'Radio'}
            disabled={disabled}
            onValueChange={(value) => onChange({ type: value ?? 'Radio' })}
          >
            <SelectTrigger data-testid="ics204-edit-communication-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="dark">
              <SelectItem value="Radio">Radio</SelectItem>
              <SelectItem value="Nonradio">Nonradio</SelectItem>
            </SelectContent>
          </Select>
        </TableCell>
        <TableCell>
          <Input
            value={radioOnly ? (draft.function ?? '') : (draft.other ?? '')}
            disabled={disabled}
            data-testid="ics204-edit-communication-function"
            aria-invalid={
              error &&
              !(radioOnly ? draft.function?.trim() : draft.other?.trim())
                ? true
                : undefined
            }
            onChange={(e) =>
              onChange(
                radioOnly
                  ? { function: e.target.value }
                  : { other: e.target.value }
              )
            }
          />
        </TableCell>
        <TableCell>
          <Input
            value={draft.channel_name ?? ''}
            disabled={disabled}
            data-testid="ics204-edit-communication-channel-name"
            aria-invalid={
              error && !draft.channel_name?.trim() ? true : undefined
            }
            onChange={(e) => onChange({ channel_name: e.target.value })}
          />
        </TableCell>
        <TableCell>
          {radioOnly ? (
            <Input
              value={draft.channel_number ?? ''}
              disabled={disabled}
              data-testid="ics204-edit-communication-channel-number"
              onChange={(e) => onChange({ channel_number: e.target.value })}
            />
          ) : (
            <Input
              value={draft.cell_phone_number ?? ''}
              disabled={disabled}
              data-testid="ics204-edit-communication-cell-phone"
              onChange={(e) => onChange({ cell_phone_number: e.target.value })}
            />
          )}
        </TableCell>
        <TableCell>
          {radioOnly ? (
            <Input
              value={draft.rx_frequency ?? ''}
              disabled={disabled}
              data-testid="ics204-edit-communication-rx-frequency"
              onChange={(e) => onChange({ rx_frequency: e.target.value })}
            />
          ) : (
            <span className={ICS204_MUTED}>—</span>
          )}
        </TableCell>
        <TableCell>
          {radioOnly ? (
            <Input
              value={draft.rx_tone_nac ?? ''}
              disabled={disabled}
              data-testid="ics204-edit-communication-rx-tone"
              onChange={(e) => onChange({ rx_tone_nac: e.target.value })}
            />
          ) : (
            <span className={ICS204_MUTED}>—</span>
          )}
        </TableCell>
        <TableCell>
          {radioOnly ? (
            <Input
              value={draft.tx_frequency ?? ''}
              disabled={disabled}
              data-testid="ics204-edit-communication-tx-frequency"
              onChange={(e) => onChange({ tx_frequency: e.target.value })}
            />
          ) : (
            <span className={ICS204_MUTED}>—</span>
          )}
        </TableCell>
        <TableCell>
          {radioOnly ? (
            <Input
              value={draft.tx_tone_nac ?? ''}
              disabled={disabled}
              data-testid="ics204-edit-communication-tx-tone"
              onChange={(e) => onChange({ tx_tone_nac: e.target.value })}
            />
          ) : (
            <span className={ICS204_MUTED}>—</span>
          )}
        </TableCell>
        <TableCell>
          {radioOnly ? (
            <Input
              value={draft.mode ?? ''}
              disabled={disabled}
              data-testid="ics204-edit-communication-mode"
              onChange={(e) => onChange({ mode: e.target.value })}
            />
          ) : (
            <span className={ICS204_MUTED}>—</span>
          )}
        </TableCell>
        <TableCell>
          <Input
            value={draft.remarks ?? ''}
            disabled={disabled}
            data-testid="ics204-edit-communication-remarks"
            onChange={(e) => onChange({ remarks: e.target.value })}
          />
        </TableCell>
        <TableCell>
          <div className="tw:flex tw:items-center tw:justify-end tw:gap-1">
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label="Confirm communication"
              className={ICS204_ICON_BTN}
              data-testid="ics204-edit-confirm-communication"
              disabled={disabled}
              onClick={onConfirm}
            >
              <Check className="tw:h-4 tw:w-4" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label="Cancel communication"
              className={ICS204_DELETE_BTN}
              data-testid="ics204-edit-cancel-communication"
              disabled={disabled}
              onClick={onCancel}
            >
              <X className="tw:h-4 tw:w-4" />
            </Button>
          </div>
        </TableCell>
      </TableRow>
      {error ? (
        <TableRow>
          <TableCell
            colSpan={COLUMNS.length}
            className="tw:py-1 tw:text-xs tw:text-destructive"
            data-testid="ics204-edit-communication-error"
          >
            {error}
          </TableCell>
        </TableRow>
      ) : null}
    </>
  );
};

export default CommunicationsSection;
